import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { resolveNextPath, getPublicSiteUrl } from '@/lib/authRedirect';

import { useAuth } from '@/context/AuthContext';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

/**
 * QA checklist:
 *  1) /app/producer/... → guard → /login → email login → back to /app/producer/...
 *  2) Same with Google OAuth
 *  3) Forgot password from /app/producer/... → reset → back
 *  4) Direct /login when logged in → /app/producer (host) or /explore
 *  5) next=https://evil.com → ignored, fallback used
 */

// Google icon as simple SVG component
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

type ViewState = 'main' | 'email-form' | 'forgot-password' | 'forgot-password-sent' | 'confirm-email';
type LoadingState = 'idle' | 'loading' | 'google-loading' | 'facebook-loading' | 'resending';

function normalizeMsg(s: unknown) {
  return String(s ?? '').toLowerCase().trim();
}

function friendlyAuthErrorMessage(error: unknown) {
  const message = normalizeMsg((error as any)?.message ?? error);

  if (!message) return 'Något gick fel. Försök igen.';

  if (message.includes('invalid login credentials')) return 'Fel e-post eller lösenord.';
  if (message.includes('email not confirmed')) return 'E-posten är inte bekräftad ännu.';
  if (message.includes('user already registered')) return 'E-postadressen är redan registrerad. Försök logga in istället.';
  if (message.includes('invalid email')) return 'Ogiltig e-postadress.';
  if (message.includes('password should be') || message.includes('weak password'))
    return 'Lösenordet uppfyller inte kraven. Försök med ett starkare lösenord.';
  if (message.includes('too many requests')) return 'För många försök. Vänta en stund och prova igen.';
  if (message.includes('captcha')) return 'Verifiering krävs. Försök igen om en stund.';

  return (error as any)?.message ?? 'Något gick fel. Försök igen.';
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [viewState, setViewState] = useState<ViewState>('main');
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') === 'signup' ? 'signup' : 'login';
  });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [pendingConfirmEmail, setPendingConfirmEmail] = useState('');
  const [showResendOnLogin, setShowResendOnLogin] = useState(false);
  const [showLoginHelp, setShowLoginHelp] = useState(false);
  const [signupHelp, setSignupHelp] = useState<null | { kind: 'weak_password' | 'already_registered'; title: string; body: string }>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [checkingConfirm, setCheckingConfirm] = useState(false);
  const [resetSentHint, setResetSentHint] = useState<string | null>(null);
  const [resetResending, setResetResending] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();

  // TODO: when isHost info is available in AuthContext, pass it here for smart fallback
  const isHost = false;

  // Compute intended destination once, stable across renders
  const nextPath = resolveNextPath({
    fromState: (location.state as any)?.from ?? null,
    search: window.location.search,
    isHost,
  });

  const PUBLIC_SITE_URL = getPublicSiteUrl();

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate(nextPath, { replace: true });
    }
  }, [user, authLoading, navigate, nextPath]);

  const handleResendConfirmation = async (emailToResend: string) => {
    setLoadingState('resending');

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: emailToResend,
        options: {
          emailRedirectTo: `${PUBLIC_SITE_URL}/auth/callback?next=${encodeURIComponent(nextPath)}`,
        },
      });

      if (error) {
        toast({
          title: 'Kunde inte skicka bekräftelse',
          description: friendlyAuthErrorMessage(error),
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Ny bekräftelselänk skickad',
          description: 'Kolla din inkorg (och skräpposten).',
        });
      }
    } catch (err) {
      toast({
        title: 'Något gick fel',
        description: friendlyAuthErrorMessage(err),
        variant: 'destructive',
      });
    } finally {
      setLoadingState('idle');
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoadingState('loading');
    setShowResendOnLogin(false);
    setShowLoginHelp(false);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const msg = normalizeMsg(error.message);
        const isEmailNotConfirmed = msg.includes('email not confirmed');
        const isInvalidCreds = msg.includes('invalid login credentials');

        if (isEmailNotConfirmed) {
          setShowResendOnLogin(true);
          toast({
            title: 'Inloggning misslyckades',
            description: friendlyAuthErrorMessage(error),
            variant: 'destructive',
          });
        } else if (isInvalidCreds) {
          setShowLoginHelp(true);
          toast({
            title: 'Det gick inte att logga in',
            description: 'Kontrollera e-post och lösenord. Om du är ny här kan du skapa konto – annars kan du återställa lösenordet.',
          });
        } else {
          toast({
            title: 'Inloggning misslyckades',
            description: friendlyAuthErrorMessage(error),
            variant: 'destructive',
          });
        }

        setLoadingState('idle');
        return;
      }

      toast({
        title: 'Inloggad!',
        description: 'Välkommen tillbaka.',
      });
      // Navigation handled by useEffect watching `user`
    } catch (err) {
      toast({
        title: 'Något gick fel',
        description: friendlyAuthErrorMessage(err),
        variant: 'destructive',
      });
      setLoadingState('idle');
    }
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    setSignupHelp(null);

    // Client-side guard: minimum 8 characters
    if (password.length < 8) {
      setSignupHelp({
        kind: 'weak_password',
        title: 'Välj ett lite starkare lösenord',
        body: 'Minst 8 tecken. Gärna en fras eller tre ord. Ex: skog-kaffe-stig-2026.',
      });
      toast({
        title: 'Lösenordet är för kort',
        description: 'Minst 8 tecken. Du kan gärna använda en fras.',
      });
      return;
    }

    setLoadingState('loading');

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${PUBLIC_SITE_URL}/auth/callback?next=${encodeURIComponent(nextPath)}`,
        },
      });

      if (error) {
        const msg = normalizeMsg(error.message);
        const isWeakPassword = msg.includes('password should be') || msg.includes('weak password');
        const isAlreadyRegistered = msg.includes('user already registered');

        if (isWeakPassword) {
          setSignupHelp({
            kind: 'weak_password',
            title: 'Välj ett lite starkare lösenord',
            body: 'Minst 8 tecken. Gärna en fras eller tre ord. Ex: skog-kaffe-stig-2026.',
          });
          toast({
            title: 'Lösenordet behöver vara starkare',
            description: 'Minst 8 tecken. Tips: en mening är ofta starkare än ett krångligt ord.',
          });
        } else if (isAlreadyRegistered) {
          setSignupHelp({
            kind: 'already_registered',
            title: 'Du verkar redan ha ett konto',
            body: 'Prova att logga in istället. Om du glömt lösenordet kan du återställa det.',
          });
          toast({
            title: 'E-postadressen är redan registrerad',
            description: 'Prova att logga in istället.',
          });
        } else {
          toast({
            title: 'Kunde inte skapa konto',
            description: friendlyAuthErrorMessage(error),
            variant: 'destructive',
          });
        }

        setLoadingState('idle');
        return;
      }

      if (data.session === null) {
        setPendingConfirmEmail(email);
        setViewState('confirm-email');
      } else {
        toast({
          title: 'Konto skapat!',
          description: 'Välkommen till Farmly.',
        });
      }

      setLoadingState('idle');
    } catch (err) {
      toast({
        title: 'Något gick fel',
        description: friendlyAuthErrorMessage(err),
        variant: 'destructive',
      });
      setLoadingState('idle');
    }
  };

  const handleGoogleLogin = async () => {
    setLoadingState('google-loading');

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${PUBLIC_SITE_URL}/auth/callback?next=${encodeURIComponent(nextPath)}`,
        },
      });

      if (error) {
        toast({
          title: 'Google-inloggning misslyckades',
          description: error.message || 'Kunde inte ansluta till Google.',
          variant: 'destructive',
        });
        setLoadingState('idle');
        return;
      }
    } catch {
      toast({
        title: 'Något gick fel',
        description: 'Försök igen senare.',
        variant: 'destructive',
      });
      setLoadingState('idle');
    }
  };

  const handleFacebookLogin = async () => {
    setLoadingState('facebook-loading');

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${PUBLIC_SITE_URL}/auth/callback?next=${encodeURIComponent(nextPath)}`,
        },
      });

      if (error) {
        toast({
          title: 'Facebook-inloggning misslyckades',
          description: error.message || 'Kunde inte ansluta till Facebook.',
          variant: 'destructive',
        });
        setLoadingState('idle');
        return;
      }
    } catch {
      toast({
        title: 'Något gick fel',
        description: 'Försök igen senare.',
        variant: 'destructive',
      });
      setLoadingState('idle');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoadingState('loading');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: `${PUBLIC_SITE_URL}/auth/reset-password?next=${encodeURIComponent(nextPath)}`,
      });

      if (error) {
        toast({
          title: 'Kunde inte skicka återställningslänk',
          description: friendlyAuthErrorMessage(error),
          variant: 'destructive',
        });
        setLoadingState('idle');
        return;
      }

      setViewState('forgot-password-sent');
      setResetSentHint(null);
      toast({
        title: 'Kolla din e-post',
        description: 'Om det finns ett konto kopplat till adressen får du ett mejl med en länk.',
      });
      setLoadingState('idle');
    } catch (err) {
      toast({
        title: 'Något gick fel',
        description: friendlyAuthErrorMessage(err),
        variant: 'destructive',
      });
      setLoadingState('idle');
    }
  };

  const resetToMain = () => {
    setViewState('main');
    setForgotPasswordEmail('');
    setPendingConfirmEmail('');
    setShowResendOnLogin(false);
    setPassword('');
    setResetSentHint(null);
    setResetResending(false);
    setSignupHelp(null);
    setShowLoginHelp(false);
  };

  const showEmailForm = () => {
    setViewState('email-form');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Confirm email view (after signup with Confirm email ON)
  if (viewState === 'confirm-email') {
    const handleCheckConfirm = async () => {
      setCheckingConfirm(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate(nextPath, { replace: true });
        } else {
          toast({
            title: 'Inte klart ännu',
            description: 'Öppna länken i mejlet för att aktivera kontot.',
          });
        }
      } catch {
        toast({
          title: 'Inte klart ännu',
          description: 'Öppna länken i mejlet för att aktivera kontot.',
        });
      } finally {
        setCheckingConfirm(false);
      }
    };

    const handleResendWithSuccess = async () => {
      setResendSuccess(false);
      await handleResendConfirmation(pendingConfirmEmail);
      // If no error was thrown the toast already showed success; also set inline flag
      setResendSuccess(true);
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Bekräfta din e-post</CardTitle>
            <CardDescription>
              Vi har skickat en bekräftelselänk till{' '}
              <span className="font-medium text-foreground">{pendingConfirmEmail}</span>.
              {' '}Öppna mejlet och klicka på länken för att aktivera kontot.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md border border-border bg-muted/50 p-3">
              <p className="text-sm text-foreground font-medium">Nästa steg</p>
              <p className="text-sm text-muted-foreground mt-1">
                Öppna mejlet och klicka på länken. Sedan är du inne.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Hittar du inget mejl? Kolla skräpposten. Ibland tar det upp till en minut.
              </p>
            </div>

            <Button
              className="w-full"
              onClick={() => { window.location.href = 'mailto:'; }}
            >
              Öppna e-post
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleCheckConfirm}
              disabled={checkingConfirm}
            >
              {checkingConfirm ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kontrollerar...
                </>
              ) : (
                'Jag har klickat på länken'
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full"
              onClick={handleResendWithSuccess}
              disabled={loadingState === 'resending'}
            >
              {loadingState === 'resending' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Skickar...
                </>
              ) : (
                'Skicka ny länk'
              )}
            </Button>

            {resendSuccess && (
              <p className="text-xs text-center text-muted-foreground">
                Ny länk skickad. Kolla inkorg och skräppost (kan ta upp till en minut).
              </p>
            )}

            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => {
                setViewState('main');
                setActiveTab('signup');
                setPendingConfirmEmail('');
                setResendSuccess(false);
                setEmail('');
                setPassword('');
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Byt e-postadress
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password view
  if (viewState === 'forgot-password' || viewState === 'forgot-password-sent') {
    const handleResendReset = async () => {
      setResetResending(true);
      setResetSentHint(null);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
          redirectTo: `${PUBLIC_SITE_URL}/auth/reset-password?next=${encodeURIComponent(nextPath)}`,
        });
        if (error) {
          toast({
            title: 'Kunde inte skicka länk',
            description: friendlyAuthErrorMessage(error),
            variant: 'destructive',
          });
        } else {
          setResetSentHint('Ny länk skickad. Kolla inkorg och skräppost (kan ta upp till en minut).');
        }
      } catch (err) {
        toast({
          title: 'Något gick fel',
          description: friendlyAuthErrorMessage(err),
          variant: 'destructive',
        });
      } finally {
        setResetResending(false);
      }
    };

    const isSent = viewState === 'forgot-password-sent';

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <Link to="/" className="mx-auto mb-4">
              <span className="font-display text-2xl font-medium text-foreground">Farmly</span>
            </Link>
            <CardTitle className="text-xl">
              {isSent ? 'Återställningslänk skickad' : 'Glömt lösenord'}
            </CardTitle>
            <CardDescription>
              {isSent
                ? 'Om det finns ett konto kopplat till adressen har vi skickat ett mejl med en länk för att sätta ett nytt lösenord.'
                : 'Ange din e-postadress så skickar vi en återställningslänk. Om det finns ett konto kopplat till adressen får du ett mejl med instruktioner.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isSent && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">E-postadress</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => {
                      setForgotPasswordEmail(e.target.value);
                      setResetSentHint(null);
                    }}
                    placeholder="din@epost.se"
                    autoComplete="email"
                    disabled={loadingState === 'loading'}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loadingState === 'loading'}>
                  {loadingState === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Skickar...
                    </>
                  ) : (
                    'Skicka återställningslänk'
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Det kan ta upp till en minut. Kolla även skräpposten.
                </p>
              </form>
            )}

            {isSent && (
              <>
                <div className="space-y-2">
                  <Label>E-postadress</Label>
                  <Input
                    type="email"
                    value={forgotPasswordEmail}
                    readOnly
                    disabled
                    className="bg-muted"
                  />
                </div>

                <div className="rounded-md border border-border bg-muted/50 p-3">
                  <p className="text-sm text-foreground font-medium">Nästa steg</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Öppna mejlet och klicka på länken för att välja ett nytt lösenord.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Hittar du inget mejl? Kolla skräpposten. Ibland tar det upp till en minut.
                  </p>
                </div>

                <Button
                  className="w-full"
                  onClick={() => { window.location.href = 'mailto:'; }}
                >
                  Öppna e-post
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleResendReset}
                  disabled={resetResending}
                >
                  {resetResending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Skickar...
                    </>
                  ) : (
                    'Skicka igen'
                  )}
                </Button>

                {resetSentHint && (
                  <p className="text-xs text-center text-muted-foreground">
                    {resetSentHint}
                  </p>
                )}
              </>
            )}

            <Button variant="outline" className="w-full" onClick={resetToMain}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till inloggning
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Entry view — Facebook primary, Google secondary, email tertiary
  if (viewState === 'main') {
    const socialDisabled = loadingState === 'google-loading' || loadingState === 'facebook-loading';

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="mx-auto mb-4">
              <span className="font-display text-2xl font-medium text-foreground">Farmly</span>
            </Link>
            <CardTitle className="text-xl">Välkommen till Farmly</CardTitle>
            <CardDescription className="mt-1">
              Logga in för att spara, boka och följa det som händer nära dig.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-4">
            {/* Facebook — primary */}
            <Button
              type="button"
              className="w-full h-12 text-base"
              onClick={handleFacebookLogin}
              disabled={socialDisabled}
            >
              {loadingState === 'facebook-loading' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ansluter...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Fortsätt med Facebook
                </>
              )}
            </Button>

            {/* Google — secondary */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base"
              onClick={handleGoogleLogin}
              disabled={socialDisabled}
            >
              {loadingState === 'google-loading' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Ansluter...
                </>
              ) : (
                <>
                  <GoogleIcon className="h-5 w-5 mr-2" />
                  Fortsätt med Google
                </>
              )}
            </Button>

            {/* Email — tertiary */}
            <Button
              type="button"
              variant="ghost"
              className="w-full h-10 text-sm text-muted-foreground"
              onClick={showEmailForm}
            >
              Använd e-post istället
            </Button>

            {/* Reassurance */}
            <p className="text-xs text-center text-muted-foreground pt-2">
              Vi delar aldrig något på dina sociala medier.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email form view
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
        <CardHeader className="text-center">
          <Link to="/" className="mx-auto mb-4">
            <span className="font-display text-2xl font-medium text-foreground">Farmly</span>
          </Link>
          <CardTitle className="text-xl">Logga in eller skapa konto</CardTitle>
          <CardDescription>Med e-postadress och lösenord.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tabs for Login/Signup */}
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              setActiveTab(v as 'login' | 'signup');
              setShowResendOnLogin(false);
              setShowLoginHelp(false);
              setSignupHelp(null);
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Logga in</TabsTrigger>
              <TabsTrigger value="signup">Skapa konto</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4 mt-4">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">E-postadress</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setShowLoginHelp(false); }}
                    placeholder="din@epost.se"
                    autoComplete="email"
                    autoFocus
                    disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Lösenord</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setShowLoginHelp(false); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading'}
                  />
                </div>

                {/* Show resend confirmation when email not confirmed */}
                {showResendOnLogin && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950">
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                      Du behöver bekräfta din e-post. Kolla inkorgen (och skräpposten).
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => handleResendConfirmation(email)}
                      disabled={loadingState === 'resending' || !email}
                    >
                      {loadingState === 'resending' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Skickar...
                        </>
                      ) : (
                        'Skicka ny bekräftelse'
                      )}
                    </Button>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading' || !email || !password}
                >
                  {loadingState === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loggar in...
                    </>
                  ) : (
                    'Logga in'
                  )}
                </Button>
              </form>

              {/* Inline help after invalid credentials */}
              {showLoginHelp && activeTab === 'login' && (
                <div className="rounded-md border border-border bg-muted/50 p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Är du ny här?</p>
                  <p className="text-sm text-muted-foreground">
                    Skapa ett konto med samma e-postadress – det tar under en minut. Har du loggat in med Google tidigare? Använd knappen ovan.
                  </p>
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setShowLoginHelp(false);
                        setPassword('');
                        setActiveTab('signup');
                      }}
                    >
                      Skapa konto
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setShowLoginHelp(false);
                        setForgotPasswordEmail(email);
                        setViewState('forgot-password');
                      }}
                    >
                      Återställ lösenord
                    </Button>
                  </div>
                </div>
              )}

              <Button
                type="button"
                variant="link"
                className="w-full text-sm"
                onClick={() => {
                  setForgotPasswordEmail(email);
                  setViewState('forgot-password');
                }}
              >
                Glömt lösenord?
              </Button>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4 mt-4">
              <form onSubmit={handleEmailSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">E-postadress</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setSignupHelp(null); }}
                    placeholder="din@epost.se"
                    autoComplete="email"
                    disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Lösenord</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setSignupHelp(null); }}
                    placeholder="Välj ett lösenord"
                    autoComplete="new-password"
                    disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading'}
                  />
                </div>

                {/* Inline signup help */}
                {signupHelp?.kind === 'weak_password' && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950 space-y-1.5">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{signupHelp.title}</p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">{signupHelp.body}</p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">Tips: en mening är ofta starkare än ett krångligt ord.</p>
                  </div>
                )}

                {signupHelp?.kind === 'already_registered' && (
                  <div className="rounded-md border border-border bg-muted/50 p-4 space-y-3">
                    <p className="text-sm font-medium text-foreground">{signupHelp.title}</p>
                    <p className="text-sm text-muted-foreground">{signupHelp.body}</p>
                    <div className="flex flex-col gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setSignupHelp(null);
                          setPassword('');
                          setActiveTab('login');
                        }}
                      >
                        Gå till Logga in
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setSignupHelp(null);
                          setForgotPasswordEmail(email);
                          setViewState('forgot-password');
                        }}
                      >
                        Återställ lösenord
                      </Button>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loadingState === 'loading' || loadingState === 'google-loading' || loadingState === 'facebook-loading' || !email || !password}
                >
                  {loadingState === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Skapar konto...
                    </>
                  ) : (
                    'Skapa konto'
                  )}
                </Button>
              </form>

              <p className="text-xs text-center text-muted-foreground">
                Genom att fortsätta godkänner du våra användarvillkor och integritetspolicy.
              </p>
            </TabsContent>
          </Tabs>

          {/* Back to Google entry */}
          <Button
            type="button"
            variant="ghost"
            className="w-full text-sm"
            onClick={resetToMain}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
